/**
 * `/api/qa/systems` (YAG-1108): the workspace's declared systems, each a named
 * YAML saying which repositories run together. A Team picks one in its
 * settings; a build on that Team starts inside it.
 */
import type {
  DeclaredSystem,
  DeclaredSystemSummary,
  DeclaredSystemValidateResult,
  DeclaredSystemValidateSkip,
  DeclaredSystemValidation,
} from "@yagni/shared";
import type { YamlEditorError } from "./YamlEditor.js";

export type {
  DeclaredSystem,
  DeclaredSystemSummary,
  DeclaredSystemValidateResult,
  DeclaredSystemValidation,
};

const BASE = "/api/qa/systems";

/** The name rule the server holds too: a lowercase slug of at most 40. */
export const SYSTEM_NAME_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;

export const SYSTEMS_REQUEST_FAILED = "The systems request failed.";
export const SYSTEM_YAML_INVALID = "system_yaml_invalid";
export const SYSTEM_EXISTS = "declared_system_exists";
export const SYSTEM_CHANGED = "declared_system_changed";

/** Said on the row and atop the sheet of a system whose saved configuration does not read. */
export const SYSTEM_CONFIG_UNREADABLE =
  "Could not read this system's saved configuration; edit and save it again.";
/** A save refused because someone else saved first; the sheet keeps the person's text. */
export const SYSTEM_CHANGED_LINE =
  "Someone saved this system meanwhile; your text is kept, save again to overwrite.";
/** Said in place of the schema's raw complaint about an empty `repos:`. */
export const SYSTEM_REPOS_EMPTY_LINE = "List at least one repository under repos:";

export class SystemsApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The machine-readable reason, when the server named one. */
    readonly code?: string,
    /** The parsed error body, when there was one. */
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

async function request<T>(
  url: string,
  init: { method?: "POST" | "PUT" | "DELETE"; body?: unknown } = {},
): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    ...(init.method ? { method: init.method } : {}),
    ...(init.body === undefined
      ? {}
      : {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(init.body),
        }),
  });
  if (response.status === 204) return undefined as T;
  // A proxy or a signed-out redirect answers with HTML: the status line is the
  // honest thing left to say, never the parse error.
  let parsed: unknown;
  let readable = true;
  try {
    parsed = await response.json();
  } catch {
    readable = false;
  }
  const data = (parsed ?? {}) as Record<string, unknown>;
  if (!response.ok) {
    const code = errorCodeOf(data);
    const message = errorMessageOf(data) ?? (response.statusText || SYSTEMS_REQUEST_FAILED);
    throw new SystemsApiError(
      readable ? message : response.statusText || SYSTEMS_REQUEST_FAILED,
      response.status,
      readable ? code : undefined,
      readable ? data : undefined,
    );
  }
  if (!readable) throw new SystemsApiError(SYSTEMS_REQUEST_FAILED, response.status);
  return parsed as T;
}

/** The machine-readable reason an error body names: `code`, else `error`. */
function errorCodeOf(data: Record<string, unknown>): string | undefined {
  if (typeof data.code === "string") return data.code;
  return typeof data.error === "string" ? data.error : undefined;
}

/** The sentence an error body carries: `message`, else `error`. */
function errorMessageOf(data: Record<string, unknown>): string | undefined {
  if (typeof data.message === "string") return data.message;
  return typeof data.error === "string" ? data.error : undefined;
}

export async function listSystems(): Promise<DeclaredSystemSummary[]> {
  const body = await request<{ systems?: unknown }>(BASE);
  return Array.isArray(body?.systems) ? (body.systems as DeclaredSystemSummary[]) : [];
}

export async function readSystem(id: string): Promise<DeclaredSystem> {
  const body = await request<{ system: DeclaredSystem }>(`${BASE}/${encodeURIComponent(id)}`);
  return body.system;
}

export async function createSystem(input: { name: string; text: string }): Promise<DeclaredSystem> {
  const body = await request<{ system?: DeclaredSystem } & Partial<DeclaredSystem>>(BASE, {
    method: "POST",
    body: input,
  });
  return (body.system ?? body) as DeclaredSystem;
}

export async function updateSystem(
  id: string,
  input: { text: string; expectedRevision: number },
): Promise<DeclaredSystem> {
  const body = await request<{ system?: DeclaredSystem } & Partial<DeclaredSystem>>(
    `${BASE}/${encodeURIComponent(id)}`,
    { method: "PUT", body: input },
  );
  return (body.system ?? body) as DeclaredSystem;
}

export async function deleteSystem(id: string): Promise<void> {
  await request<void>(`${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/**
 * Validate now on a system (YAG-1109): one run per member that is not shared
 * and has an environment, each without repair. The answer names the runs it
 * started and the members it skipped, with why.
 */
export async function validateSystem(id: string): Promise<DeclaredSystemValidateResult> {
  const body = await request<Partial<DeclaredSystemValidateResult>>(
    `${BASE}/${encodeURIComponent(id)}/validate`,
    { method: "POST", body: {} },
  );
  return {
    runs: Array.isArray(body?.runs) ? body.runs : [],
    skipped: Array.isArray(body?.skipped) ? body.skipped : [],
  };
}

/** Why a member got no run, in the sheet's words. */
export const VALIDATE_SKIP_WORDS: Record<DeclaredSystemValidateSkip, string> = {
  shared: "shared, so it runs with the members that use it",
  no_environment: "no environment set up yet",
  running: "a run is already going",
  unavailable: "not reachable from this workspace",
};

export function validateSkipWord(reason: string): string {
  return VALIDATE_SKIP_WORDS[reason as DeclaredSystemValidateSkip] ?? "not validated";
}

/** A starter YAML listing the workspace's connected repositories, nothing picked. */
export async function readSystemTemplate(): Promise<string> {
  const body = await request<{ yaml?: { text?: unknown } }>(`${BASE}/template`);
  return typeof body.yaml?.text === "string" ? body.yaml.text : "";
}

/** What a refused save says beside the text. */
export function systemErrorOf(err: unknown): YamlEditorError {
  if (!(err instanceof SystemsApiError)) return { message: SYSTEMS_REQUEST_FAILED };
  if (err.code === SYSTEM_EXISTS) return { message: "A system already has that name." };
  if (err.code === SYSTEM_CHANGED) return { message: SYSTEM_CHANGED_LINE };
  const line = err.details?.line;
  const at = typeof line === "number" ? { line } : {};
  if (isEmptyReposComplaint(err)) return { message: SYSTEM_REPOS_EMPTY_LINE, ...at };
  return { message: err.message, ...at };
}

/**
 * The template's `repos:` with every line commented out parses as null, and
 * the schema says "expected record, received null at repos": a person reads
 * that as noise, so the sheet says what to do instead.
 */
function isEmptyReposComplaint(err: SystemsApiError): boolean {
  if (err.code !== SYSTEM_YAML_INVALID) return false;
  return (
    /received (null|undefined)/.test(err.message) &&
    /\bat repos(?: \(line \d+\))?$/.test(err.message)
  );
}
